import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tags, Plus, Trash2, Pencil, X, Check, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import api from '../utils/axios';
import {
  COLOR_OPTIONS, ICON_OPTIONS, COLOR_CLASSES, badgeClass, iconComp,
} from '../lib/caseStatusMeta';

// Estados fijos del sistema (solo lectura, para referencia)
const SYSTEM_REF = [
  'Abierto', 'Pendiente', 'En Investigación', 'Pendiente Aprobación', 'En Revisión',
  'Escalado', 'Esperando Compliance', 'Bloqueo Preventivo', 'En Vigilancia', 'Testing',
  'Cerrado', 'Cancelado', 'Archivado',
];

const emptyForm = { label: '', color: 'slate', icon: 'Flag' };

const StatusBadge = ({ label, color, icon }) => {
  const Icon = iconComp(icon);
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-semibold inline-flex items-center gap-1 ${badgeClass(color)}`}>
      <Icon size={12} />{label || 'Vista previa'}
    </span>
  );
};

export default function CaseStatusesAdmin() {
  const navigate = useNavigate();
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/case-statuses/admin');
      setStatuses(r.data?.statuses || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al cargar estados');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const resetForm = () => { setForm(emptyForm); setEditingId(null); };

  const handleSubmit = async () => {
    if (!form.label.trim() || form.label.trim().length < 2) { toast.error('El nombre debe tener al menos 2 caracteres'); return; }
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/case-statuses/${editingId}`, { label: form.label, color: form.color, icon: form.icon });
        toast.success('Estado actualizado');
      } else {
        await api.post('/case-statuses', { label: form.label, color: form.color, icon: form.icon });
        toast.success('Estado creado');
      }
      resetForm();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (s) => {
    setEditingId(s.id);
    setForm({ label: s.label, color: s.color, icon: s.icon });
  };

  const handleDelete = async (s) => {
    if (s.en_uso > 0) {
      toast.error(`No se puede borrar: ${s.en_uso} caso(s) usan «${s.label}». Movélos a otro estado primero.`);
      return;
    }
    if (!window.confirm(`¿Borrar el estado «${s.label}»?`)) return;
    try {
      await api.delete(`/case-statuses/${s.id}`);
      toast.success('Estado borrado');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo borrar');
    }
  };

  const toggleActivo = async (s) => {
    try {
      await api.put(`/case-statuses/${s.id}`, { activo: !s.activo });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo actualizar');
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="case-statuses-admin">
      <div className="flex items-center gap-3">
        <Button variant="outline" size="sm" onClick={() => navigate(-1)} data-testid="back-btn">
          <ArrowLeft size={16} className="mr-1" /> Volver
        </Button>
        <div className="flex items-center gap-2">
          <Tags className="text-indigo-600" size={24} />
          <h1 className="text-2xl font-bold text-slate-800">Estados de casos</h1>
        </div>
      </div>
      <p className="text-sm text-slate-500">
        Creá estados personalizados para asignarlos a tus casos (en la lista, el detalle y el tablero Kanban).
        Los estados fijos del sistema no se pueden editar ni borrar.
      </p>

      {/* Formulario crear/editar */}
      <Card className="p-5">
        <h2 className="text-sm font-semibold text-slate-700 mb-4">
          {editingId ? 'Editar estado' : 'Nuevo estado'}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          <div className="md:col-span-1">
            <Label className="text-sm">Nombre</Label>
            <Input
              value={form.label}
              onChange={e => setForm(p => ({ ...p, label: e.target.value }))}
              placeholder="Ej. Revisión Legal"
              className="mt-1"
              data-testid="status-label-input"
            />
          </div>
          <div>
            <Label className="text-sm">Color</Label>
            <select
              value={form.color}
              onChange={e => setForm(p => ({ ...p, color: e.target.value }))}
              className="mt-1 w-full text-sm border border-slate-200 rounded-md px-2 py-2 bg-white"
              data-testid="status-color-select"
            >
              {COLOR_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-sm">Ícono</Label>
            <select
              value={form.icon}
              onChange={e => setForm(p => ({ ...p, icon: e.target.value }))}
              className="mt-1 w-full text-sm border border-slate-200 rounded-md px-2 py-2 bg-white"
              data-testid="status-icon-select"
            >
              {ICON_OPTIONS.map(i => <option key={i} value={i}>{i}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={handleSubmit} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="status-save-btn">
              {editingId ? <Check size={16} className="mr-1" /> : <Plus size={16} className="mr-1" />}
              {saving ? 'Guardando...' : (editingId ? 'Guardar' : 'Crear')}
            </Button>
            {editingId && (
              <Button variant="outline" onClick={resetForm} data-testid="status-cancel-edit-btn"><X size={16} /></Button>
            )}
          </div>
        </div>
        {/* Selección de color visual + vista previa */}
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <span className="text-xs text-slate-500">Vista previa:</span>
          <StatusBadge label={form.label} color={form.color} icon={form.icon} />
          <div className="flex flex-wrap gap-1 ml-auto">
            {COLOR_OPTIONS.map(c => (
              <button
                key={c}
                type="button"
                onClick={() => setForm(p => ({ ...p, color: c }))}
                className={`w-6 h-6 rounded-full border-2 ${COLOR_CLASSES[c].dot} ${form.color === c ? 'border-slate-800 scale-110' : 'border-white'} transition-transform`}
                title={c}
                data-testid={`color-swatch-${c}`}
              />
            ))}
          </div>
        </div>
      </Card>

      {/* Lista de estados personalizados */}
      <Card className="p-5">
        <h2 className="text-sm font-semibold text-slate-700 mb-3">Estados personalizados</h2>
        {loading ? (
          <p className="text-sm text-slate-400">Cargando...</p>
        ) : statuses.length === 0 ? (
          <p className="text-sm text-slate-400">Todavía no creaste estados personalizados.</p>
        ) : (
          <div className="space-y-2" data-testid="custom-statuses-list">
            {statuses.map(s => (
              <div key={s.id} className="flex items-center gap-3 p-2 rounded-lg border border-slate-100 hover:bg-slate-50" data-testid={`status-row-${s.id}`}>
                <StatusBadge label={s.label} color={s.color} icon={s.icon} />
                <span className="text-[11px] text-slate-400">{s.key}</span>
                {!s.activo && <span className="text-[11px] px-1.5 py-0.5 bg-slate-200 text-slate-600 rounded">inactivo</span>}
                <span className="text-[11px] text-slate-500 ml-auto">{s.en_uso} caso(s)</span>
                <Button variant="ghost" size="sm" onClick={() => toggleActivo(s)} title={s.activo ? 'Desactivar' : 'Activar'} data-testid={`status-toggle-${s.id}`}>
                  {s.activo ? 'On' : 'Off'}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => handleEdit(s)} data-testid={`status-edit-${s.id}`}><Pencil size={14} /></Button>
                <Button variant="ghost" size="sm" onClick={() => handleDelete(s)} className="text-red-600" data-testid={`status-delete-${s.id}`}><Trash2 size={14} /></Button>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Estados del sistema (referencia) */}
      <Card className="p-5">
        <h2 className="text-sm font-semibold text-slate-700 mb-3">Estados del sistema (fijos)</h2>
        <div className="flex flex-wrap gap-2">
          {SYSTEM_REF.map(l => (
            <span key={l} className="px-2 py-0.5 rounded text-xs bg-slate-100 text-slate-600 border border-slate-200">{l}</span>
          ))}
        </div>
      </Card>
    </div>
  );
}
